import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { facilities } from '../data/mockData';
import { useProperties } from '../contexts/PropertyContext';
import { useCategories } from '../contexts/CategoriesContext';
import { SlidersHorizontal, X } from 'lucide-react';

export default function Properties() {
  const [searchParams] = useSearchParams();
  const { properties, loading } = useProperties();
  const { categories, loading: categoriesLoading } = useCategories();
  const [filteredProperties, setFilteredProperties] = useState(properties);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [areaRange, setAreaRange] = useState([0, 500]);
  const [bedroomsFilter, setBedroomsFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);

  useEffect(() => {
    let filtered = [...properties];

    // Only show active properties
    filtered = filtered.filter((p: any) => p.active);

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((p: any) => p.type === typeFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((p: any) => 
        p.category.toLowerCase().includes(categoryFilter.toLowerCase()) ||
        categories.find(c => c.nameEn === categoryFilter)?.name === p.category
      );
    }

    // Price filter
    filtered = filtered.filter((p: any) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Area filter
    filtered = filtered.filter((p: any) => p.area >= areaRange[0] && p.area <= areaRange[1]);

    // Bedrooms filter
    if (bedroomsFilter !== 'all') {
      const beds = parseInt(bedroomsFilter);
      filtered = filtered.filter((p: any) => p.bedrooms >= beds);
    }

    // Facilities filter
    if (selectedFacilities.length > 0) {
      filtered = filtered.filter((p: any) => 
        selectedFacilities.every((f: any) => p.facilities.includes(f))
      );
    }

    // Sort
    switch (sortBy) {
      case 'price-high':
        filtered.sort((a: any, b: any) => b.price - a.price);
        break;
      case 'price-low':
        filtered.sort((a: any, b: any) => a.price - b.price);
        break;
      case 'newest':
        filtered.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'area-large':
        filtered.sort((a: any, b: any) => b.area - a.area);
        break;
    }

    setFilteredProperties(filtered);
  }, [typeFilter, categoryFilter, priceRange, areaRange, bedroomsFilter, sortBy, selectedFacilities, properties]);

  const toggleFacility = (facilityId: string) => {
    setSelectedFacilities(prev => 
      prev.includes(facilityId) 
        ? prev.filter(f => f !== facilityId)
        : [...prev, facilityId]
    );
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setCategoryFilter('all');
    setPriceRange([0, 10000000]);
    setAreaRange([0, 500]);
    setBedroomsFilter('all');
    setSelectedFacilities([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل العقارات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Page Header */}
      <div className="bg-[#D4AF37] text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">جميع العقارات</h1>
          <p className="text-white/80">اكتشف أفضل العقارات المتاحة للبيع والإيجار</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className={`lg:w-80 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">البحث والتصفية</h2>
                <button 
                  onClick={() => setShowFilters(false)}
                  className="lg:hidden"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Type Filter */}
              <div className="mb-6">
                <Label className="mb-2 block">نوع العملية</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="sale">للبيع</SelectItem>
                    <SelectItem value="rent">للإيجار</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <Label className="mb-2 block">نوع العقار</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الأنواع</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.nameEn}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <Label className="mb-2 block">السعر</Label>
                <div className="flex items-center gap-2 mb-3">
                  <Input
                    type="number"
                    placeholder="من"
                    value={priceRange[0] || ''}
                    onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                    className="flex-1"
                  />
                  <span className="text-gray-500">-</span>
                  <Input
                    type="number"
                    placeholder="إلى"
                    value={priceRange[1] || ''}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 10000000])}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 whitespace-nowrap">جنيه</span>
                </div>
                <Slider
                  value={priceRange}
                  onValueChange={setPriceRange}
                  min={0}
                  max={10000000}
                  step={10000}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{priceRange[0].toLocaleString('ar-EG')}</span>
                  <span>{priceRange[1].toLocaleString('ar-EG')}</span>
                </div>
              </div>

              {/* Area Range */}
              <div className="mb-6">
                <Label className="mb-2 block">المساحة</Label>
                <div className="flex items-center gap-2 mb-3">
                  <Input
                    type="number"
                    placeholder="من"
                    value={areaRange[0] || ''}
                    onChange={(e) => setAreaRange([parseInt(e.target.value) || 0, areaRange[1]])}
                    className="flex-1"
                  />
                  <span className="text-gray-500">-</span>
                  <Input
                    type="number"
                    placeholder="إلى"
                    value={areaRange[1] || ''}
                    onChange={(e) => setAreaRange([areaRange[0], parseInt(e.target.value) || 500])}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-600 whitespace-nowrap">م²</span>
                </div>
                <Slider
                  value={areaRange}
                  onValueChange={setAreaRange}
                  min={0}
                  max={500}
                  step={10}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{areaRange[0]} م²</span>
                  <span>{areaRange[1]} م²</span>
                </div>
              </div>

              {/* Bedrooms Filter */}
              <div className="mb-6">
                <Label className="mb-2 block">عدد الغرف</Label>
                <Select value={bedroomsFilter} onValueChange={setBedroomsFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">أي عدد</SelectItem>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Facilities Filter */}
              <div className="mb-6">
                <Label className="mb-3 block">المرافق</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {facilities.map(facility => (
                    <div key={facility.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`facility-${facility.id}`}
                        checked={selectedFacilities.includes(facility.id)}
                        onCheckedChange={() => toggleFacility(facility.id)}
                      />
                      <label
                        htmlFor={`facility-${facility.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        {facility.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full"
              >
                مسح الفلاتر
              </Button>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Sort and Filter Toggle */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">عدد النتائج:</span>
                <span className="font-bold text-[#8B7109]">{filteredProperties.length}</span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label htmlFor="sort">ترتيب حسب:</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">الأحدث</SelectItem>
                      <SelectItem value="price-high">السعر الأعلى</SelectItem>
                      <SelectItem value="price-low">السعر الأقل</SelectItem>
                      <SelectItem value="area-large">المساحة الأكبر</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden"
                >
                  <SlidersHorizontal className="w-5 h-5 ml-2" />
                  فلاتر
                </Button>
              </div>
            </div>

            {/* Properties Grid */}
            {filteredProperties.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProperties.map((property: any) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-600 text-lg">لم يتم العثور على عقارات تطابق معايير البحث</p>
                <Button
                  onClick={clearFilters}
                  className="bg-[#D4AF37] hover:bg-[#B8960C] text-white mt-4"
                >
                  مسح الفلاتر
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}